import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, CheckCircle, Target, Clock, TrendingUp, Brain, 
  Calendar, Timer, Zap, PhoneOff, Award, Activity, 
  Sparkles, Bell, BookOpen, Dumbbell, CheckSquare, Coffee,
  Check, Trophy, ArrowLeft
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, Tooltip, 
  LineChart, Line, AreaChart, Area,
  RadarChart, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, Radar 
} from 'recharts';
import DisciplineProgressChart from '../DigitalDiscipline/DisciplineProgressChart';
import { useDisciplineStore } from '../../contexts/disciplineStore';
import { calculateLevel } from '../../utils/xpEngine';
import LevelProgressCard from './LevelProgressCard';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  }
};

// Animated progress bar component
const AnimatedProgressBar = ({ value, color = "#00FFD1", height = 6, delay = 0 }) => (
  <div className="w-full bg-gray-800 rounded-full overflow-hidden" style={{ height }}>
    <motion.div 
      className="h-full"
      style={{ background: color }}
      initial={{ width: 0 }}
      animate={{ width: `${value}%` }}
      transition={{ 
        duration: 1.2, 
        ease: "easeOut",
        delay
      }}
    />
  </div>
);

// Stat card component for reuse
const StatCard = ({ 
  title, 
  icon, 
  value, 
  unit = "", 
  progress = null,
  trend = null, 
  color = "#00FFD1", 
  index = 0,
  isGrowing = true,
  isDarkMode = true,
  onClick = () => {}
}) => {
  return (
    <motion.div 
      className={`relative rounded-xl overflow-hidden backdrop-blur-sm ${isDarkMode ? 'bg-[#191919]' : 'bg-white'} shadow-lg`}
      whileHover={{ translateY: -5 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={onClick}
    >
      <motion.div
        className="absolute top-0 left-0 w-full h-1"
        style={{ backgroundColor: color }}
        initial={{ scaleX: 0, originX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
      />
      
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${color}20` }}>
              {React.cloneElement(icon, { size: 20, color: color, strokeWidth: 2 })}
            </div>
            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h3>
          </div>
          
          {trend !== null && (
            <div className={`text-xs px-2 py-1 rounded-full ${
              isGrowing ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
            }`}>
              {isGrowing ? '+' : ''}{trend}%
            </div>
          )}
        </div>
        
        <div className="flex items-baseline mb-2">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{value}</h2>
          {unit && <span className="ml-1 text-sm opacity-70">{unit}</span>}
        </div>
        
        {progress !== null && (
          <AnimatedProgressBar value={progress} color={color} delay={0.3 + index * 0.1} />
        )}
      </div>
    </motion.div>
  );
};

const DisciplineStatsPanel = ({ isDarkMode = true, onBackToOverview }) => {
  // Fix: Use Zustand store properly with correct variable names
  const { goals, timers, xp, streak, completedGoals } = useDisciplineStore();
  
  const [focusSessionTime, setFocusSessionTime] = useState(0);
  const [screenTimeData, setScreenTimeData] = useState([]);
  const [screenTimeReduction, setScreenTimeReduction] = useState(0);
  // Calculate real-time metrics from store
  const totalXP = xp;
  const completedGoalsCount = Array.isArray(completedGoals) ? completedGoals.length : goals.filter(goal => goal.completed).length;
  const activeGoals = goals.filter(goal => !goal.completed).length;
  const focusStreak = streak;
  
  // Calculate level information using XP engine
  const levelInfo = useMemo(() => {
    return calculateLevel(totalXP);
  }, [totalXP]);
  
  // Aggregate goals by categories
  const goalsWithCategories = useMemo(() => {
    return goals.reduce((acc, goal) => {
      const category = goal.category || 'other';
      if (!acc[category]) {
        acc[category] = { total: 0, completed: 0 };
      }
      acc[category].total++;
      if (goal.completed) {
        acc[category].completed++;
      }
      return acc;
    }, {});
  }, [goals]);

  // Load additional data from localStorage
  useEffect(() => {
    try {
      // Load focus sessions
      const sessionHistory = JSON.parse(localStorage.getItem('focusSessionHistory') || '[]');
      const totalMinutes = sessionHistory.reduce((total, session) => {
        return total + (session.duration || 0);
      }, 0);
      setFocusSessionTime(totalMinutes);
      
      // Get screen time data
      const screenTime = JSON.parse(localStorage.getItem('screenTimeData') || '[]');
      if (screenTime.length > 0) {
        setScreenTimeData(screenTime);
        
        // Calculate screen time reduction from previous week
        const thisWeekTotal = screenTime.reduce((sum, item) => sum + item.value, 0);
        const prevWeekTotal = JSON.parse(localStorage.getItem('prevWeekScreenTime') || '0');
        if (prevWeekTotal > 0) {
          const reduction = ((prevWeekTotal - thisWeekTotal) / prevWeekTotal) * 100;
          setScreenTimeReduction(Math.round(reduction));
        }
      } else {
        // Mock data for demo
        setScreenTimeData([
          { name: 'Pzt', value: 3.2 },
          { name: 'Sal', value: 2.8 },
          { name: 'Çar', value: 4.1 },
          { name: 'Per', value: 2.9 },
          { name: 'Cum', value: 3.5 },
          { name: 'Cmt', value: 5.2 },
          { name: 'Paz', value: 4.8 }
        ]);
        setScreenTimeReduction(15);
      }
      
    } catch (error) {
      console.error("Error loading stats data:", error);
    }
  }, []);
  
  // Generate streak distribution data
  const generateStreakDistribution = () => {
    return [
      { name: '1-3 gün', value: 8 },
      { name: '4-7 gün', value: 5 },
      { name: '8-14 gün', value: 3 },
      { name: '15+ gün', value: 2 }
    ];
  };
  
  // Calculate discipline score based on completed goals, active streak and screen time reduction
  const calculateDisciplineScore = () => {
    const goalScore = Math.min(completedGoalsCount * 5, 50);
    const streakScore = Math.min(focusStreak * 3, 30);
    const screenTimeScore = Math.min(Math.max(0, screenTimeReduction) * 2, 20);
    return Math.round(goalScore + streakScore + screenTimeScore);
  };
  
  const disciplineScore = calculateDisciplineScore();
    return (
    <motion.div 
      className="space-y-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div variants={itemVariants}>
          <button
            onClick={onBackToOverview}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}

      {/* Discipline Score Overview Card */}
      <motion.div 
        className={`p-4 rounded-xl glass-surface shadow-lg`}
        variants={itemVariants}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold flex items-center">
            <Shield className="h-5 w-5 mr-2 text-cyan-500" />
            Dijital Disiplin Skoru
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-cyan-900/30 text-cyan-500' : 'bg-cyan-100 text-cyan-700'
          }`}>
            {disciplineScore}/100
          </div>
        </div>
        
        <div className="mb-2 flex justify-between text-sm">
          <span>Zayıf</span>
          <span>Orta</span>
          <span>İyi</span>
          <span>Mükemmel</span>
        </div>
        
        <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-6">
          <motion.div
            className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${disciplineScore}%` }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-500/20 mr-3">
                <CheckCircle size={16} className="text-green-500" />
              </div>
              <div>
                <p className="text-sm opacity-70">Tamamlanan Hedefler</p>
                <p className="text-lg font-medium">{completedGoalsCount}</p>
              </div>
            </div>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-500/20 mr-3">
                <TrendingUp size={16} className="text-blue-500" />
              </div>
              <div>
                <p className="text-sm opacity-70">En Uzun Seri</p>
                <p className="text-lg font-medium">{focusStreak} gün</p>
              </div>
            </div>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-500/20 mr-3">
                <PhoneOff size={16} className="text-purple-500" />
              </div>
              <div>
                <p className="text-sm opacity-70">Ekran Süresi Azalma</p>
                <p className="text-lg font-medium">{screenTimeReduction}%</p>
              </div>
            </div>
          </div>
        </div>      </motion.div>

      {/* Level Progress Card */}
      <motion.div variants={itemVariants}>
        <LevelProgressCard 
          level={levelInfo.level}
          xp={levelInfo.currentXP}
          nextLevelXp={levelInfo.nextLevelXP}
          isDarkMode={isDarkMode}
        />
      </motion.div>
      
      {/* Summary Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          title="Tamamlanan Görevler" 
          icon={<CheckCircle />} 
          value={completedGoalsCount} 
          color="#10b981" 
          isDarkMode={isDarkMode} 
          index={0}
        />
        
        <StatCard 
          title="Aktif Görevler" 
          icon={<Target />} 
          value={activeGoals} 
          color="#6366f1" 
          isDarkMode={isDarkMode}
          index={1}
        />
          <StatCard 
          title="Toplam XP" 
          icon={<Award />} 
          value={totalXP} 
          unit="XP" 
          color="#f59e0b" 
          isDarkMode={isDarkMode}
          index={2}
        />
        
        <StatCard 
          title="Seviye" 
          icon={<Trophy />} 
          value={levelInfo.level} 
          unit="" 
          color="#ec4899" 
          isDarkMode={isDarkMode}
          index={3}
        />
      </div>
      
      {/* Progress and Category Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          className={`p-4 rounded-xl glass-surface shadow-lg`}
          variants={itemVariants}
        >
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <CheckSquare className="h-5 w-5 mr-2 text-green-500" />
            Hedef Tamamlama Durumu
          </h3>
          
          <DisciplineProgressChart 
            data={goals}
            chartType="overview"
            isDarkMode={isDarkMode}
          />
        </motion.div>
        
        <motion.div 
          className={`p-4 rounded-xl glass-surface shadow-lg`}
          variants={itemVariants}
        >
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-blue-500" />
            Kategori Dağılımı
          </h3>
          
          <DisciplineProgressChart 
            data={goals}
            chartType="category"
            isDarkMode={isDarkMode}
          />
        </motion.div>
      </div>
      
      {/* Daily Activity Chart */}
      <motion.div 
        className={`p-4 rounded-xl glass-surface shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-indigo-500" />
          Günlük Aktivite
        </h3>
        
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[
              { name: 'Pzt', completedTasks: 5, focusTime: 30, screenTime: 120 },
              { name: 'Sal', completedTasks: 3, focusTime: 45, screenTime: 90 },
              { name: 'Çar', completedTasks: 4, focusTime: 50, screenTime: 150 },
              { name: 'Per', completedTasks: 2, focusTime: 20, screenTime: 60 },
              { name: 'Cum', completedTasks: 6, focusTime: 80, screenTime: 180 },
              { name: 'Cmt', completedTasks: 8, focusTime: 120, screenTime: 240 },
              { name: 'Paz', completedTasks: 7, focusTime: 100, screenTime: 210 },
            ]}>
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" stroke="#6366f1" />
              <YAxis yAxisId="right" orientation="right" stroke="#ef4444" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                }}
              />
              <Bar yAxisId="left" dataKey="completedTasks" name="Tamamlanan Görevler" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="focusTime" name="Odak Süresi (dk)" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="screenTime" name="Ekran Süresi (dk)" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
      
      {/* Screen Time Distribution */}
      <motion.div
        className={`p-4 rounded-xl glass-surface shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <PhoneOff className="h-5 w-5 mr-2 text-purple-500" />
          Ekran Süresi Dağılımı
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={screenTimeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {screenTimeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`${value} dakika`]} 
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                    borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="grid grid-cols-1 content-center">
            <div className="space-y-4">
              {screenTimeData.map((entry, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className="w-4 h-4 rounded-full mr-2 flex-shrink-0"
                    style={{ backgroundColor: entry.fill }}
                  ></div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">{entry.name}</span>
                      <span className="text-sm font-medium">{entry.value} dk</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full" 
                        style={{ 
                          width: `${(entry.value / screenTimeData.reduce((a, b) => a + b.value, 0)) * 100}%`,
                          backgroundColor: entry.fill
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <h4 className="text-sm font-medium mb-2">Ekran Süresi Trendi (Son 4 Hafta)</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[
                { week: 'Hafta 1', time: 580 },
                { week: 'Hafta 2', time: 520 },
                { week: 'Hafta 3', time: 490 },
                { week: 'Hafta 4', time: 345 },
              ]}>
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip
                  formatter={(value) => [`${value} dakika`]}
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                    borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="time" 
                  name="Ekran Süresi" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ fill: '#8b5cf6', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>
      
      {/* Streak and Habits */}
      <motion.div 
        className={`p-4 rounded-xl glass-surface shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-amber-500" />
          Seri ve Alışkanlık Takibi
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium mb-3">Seri Dağılımı</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={generateStreakDistribution()}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [`${value} alışkanlık`]}
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    }}
                  />
                  <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-3">Alışkanlık Başarı Oranları</h4>
            <DisciplineProgressChart 
              data={goals}
              chartType="streak"
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </motion.div>
      
      {/* Digital Detox Achievements */}
      <motion.div
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold flex items-center">
            <Award className="h-5 w-5 mr-2 text-cyan-400" />
            Dijital Detoks Başarımları
          </h3>
          <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
            8/12 Tamamlandı
          </span>
        </div>
        
        {/* Achievement Grid with Animations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { id: 1, title: "Bildirim Detoksu", description: "1 saat boyunca bildirimsiz kal", completed: true, icon: <Bell />, color: "#3b82f6" },
            { id: 2, title: "Ekransız Sabah", description: "Sabah ilk 1 saat telefonsuz geçir", completed: true, icon: <PhoneOff />, color: "#10b981" },
            { id: 3, title: "Kitap Molası", description: "Sosyal medya yerine 30 dk kitap oku", completed: true, icon: <BookOpen />, color: "#f59e0b" },
            { id: 4, title: "Odak Modu", description: "2 saat derin odaklanma çalışması", completed: true, icon: <Target />, color: "#8b5cf6" },
            { id: 5, title: "Doğa Yürüyüşü", description: "Telefonunu kapatıp 1 saat yürüyüş", completed: true, icon: <Zap />, color: "#ec4899" },
            { id: 6, title: "Telefonsuz Yemek", description: "Telefonsuz bir öğün yemek ye", completed: true, icon: <Coffee />, color: "#14b8a6" },
            { id: 7, title: "Hafta Sonu Detoksu", description: "Cumartesi 6 saat ekransız vakit geçir", completed: true, icon: <Calendar />, color: "#f97316" },
            { id: 8, title: "Yaratıcılık Saati", description: "Dijital olmayan bir hobi için zaman ayır", completed: true, icon: <Sparkles />, color: "#6366f1" },
          ].map((achievement) => (
            <div 
              key={achievement.id}
              className={`p-3 rounded-lg relative overflow-hidden ${
                isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'
              }`}
            >
              <div className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3`}
                  style={{ backgroundColor: `${achievement.color}20` }}
                >
                  {React.cloneElement(achievement.icon, { size: 18, color: achievement.color })}
                </div>
                <div>
                  <h4 className="font-medium text-sm">{achievement.title}</h4>
                  <p className="text-xs opacity-70 mt-1">{achievement.description}</p>
                </div>
              </div>
              
              {achievement.completed && (
                <div className="absolute top-2 right-2">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-5">
          <div className="flex justify-between text-sm mb-2">
            <span>İlerleme</span>
            <span>8/12</span>
          </div>
          <div className="w-full h-2.5 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-cyan-500"
              initial={{ width: 0 }}
              animate={{ width: '67%' }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          </div>
        </div>
      </motion.div>
      
      {/* Digital vs Real-life Balance */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Brain className="h-5 w-5 mr-2 text-indigo-400" />
          Dijital vs Gerçek Hayat Dengesi
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium mb-3">Radar Grafiği</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart outerRadius={90} data={[
                  { subject: 'Fiziksel', A: 85, fullMark: 100 },
                  { subject: 'Sosyal', A: 65, fullMark: 100 },
                  { subject: 'Dijital', A: 42, fullMark: 100 },
                  { subject: 'Zihinsel', A: 78, fullMark: 100 },
                  { subject: 'Duygusal', A: 60, fullMark: 100 },
                ]}>
                  <PolarGrid stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar name="Denge Skoru" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.4} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-3">Dijital Detoks Kazanımları</h4>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Odaklanma</span>
                  <span className="text-xs font-medium text-green-400">+24%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{width: '74%'}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm">Uyku Kalitesi</span>
                  <span className="text-xs font-medium text-green-400">+18%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{width: '68%'}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm">Sosyal Etkileşim</span>
                  <span className="text-xs font-medium text-green-400">+15%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{width: '65%'}}></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm">Yaratıcılık</span>
                  <span className="text-xs font-medium text-green-400">+12%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{width: '62%'}}></div>
                </div>
              </div>
            </div>
            
            <motion.div 
              className="p-3 rounded-lg bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border border-emerald-500/20"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-emerald-500/20 mr-3">
                  <Trophy className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Haftalık Dijital Denge Başarımı</h4>
                  <p className="text-xs opacity-70 mt-1">
                    Bu hafta sosyal medya kullanımını %18 azaltarak 120 XP ve "Dijital Detox Master" rozetini kazandın!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-xs opacity-70 italic">
            "Kendi hayatının influencer'ı ol, sosyal medyanınki değil."
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DisciplineStatsPanel;